import { Row } from '@tanstack/react-table';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { FormProvider, useForm } from 'react-hook-form';
import { useTrackRenderTime } from 'src/contexts/AppPerformanceContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { AlertWithSuppressionDialog } from 'src/dialogs/AlertWithSuppressionDialog';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { getListingDetailsUpdateInput } from 'src/utils/inventoryUtils';
import { Feature, Listing, ListingDetails } from 'src/WebApiController';

import { ListingWithEvent } from '../ListingTable.types';
import { shouldResetForm, useListingPriceForm } from './ListingPriceForm.hooks';
import { ListingPricingInput } from './ListingPriceForm.types';

const ListingPricingFormRaw = ({
  rowData,
  tableRow,
  fromV2Table,
}: {
  rowData: ListingWithEvent | null;
  tableRow: React.ReactNode;
  fromV2Table?: boolean;
}) => {
  const {
    alerts,
    warningDialog,
    viagogoVirtualId,
    methods,
    onCancelSuspressionDialog,
    submitPriceChange,
  } = useListingPriceForm(rowData);

  const componentName = useMemo(() => {
    if (fromV2Table) {
      return 'ListingPricingFormV2';
    }
    return 'ListingPricingFormV1';
  }, [fromV2Table]);
  useTrackRenderTime(componentName);

  return (
    <FormProvider key={rowData?.listing?.id} {...methods}>
      {tableRow}
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        onOkay={() => {
          submitPriceChange();
          warningDialog.closeDialog();
        }}
        onCancel={onCancelSuspressionDialog}
        viagogoVirtualId={viagogoVirtualId}
      />
    </FormProvider>
  );
};

export const ListingPricingForm = memo(ListingPricingFormRaw);

type ListingPricingFormRawV2 = {
  /**
   * Row columns joint with ','
   *
   * Important!!!
   * React.memo track the props, only rerender when the props changes.
   * Dummy prop to force re-render the table row when columns/order changes.
   */
  columns: string;
  isExpanded: boolean;
  row: Row<ListingWithEvent>;
  rowIndex: number;
  getRowContent: (row: Row<ListingWithEvent>) => React.ReactNode;
  fromV2Table?: boolean;
};

const ListingPricingFormRawV2 = ({
  row,
  rowIndex,
  getRowContent,
  columns,
  isExpanded,
  fromV2Table,
}: ListingPricingFormRawV2) => {
  const rowData = useMemo(() => {
    if (row.original) {
      return {
        ...row.original,
        rowIndex: rowIndex,
      } as ListingWithEvent;
    }
    return null;
  }, [row.original, rowIndex]);

  const {
    alerts,
    warningDialog,
    viagogoVirtualId,
    methods,
    onCancelSuspressionDialog,
    submitPriceChange,
  } = useListingPriceForm(rowData);

  const componentName = useMemo(() => {
    if (fromV2Table) {
      return 'ListingPricingFormV2';
    }
    return 'ListingPricingFormV1';
  }, [fromV2Table]);
  useTrackRenderTime(componentName);

  return (
    <FormProvider key={rowData?.listing?.id} {...methods}>
      {getRowContent(row)}
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        onOkay={() => {
          submitPriceChange();
          warningDialog.closeDialog();
        }}
        onCancel={onCancelSuspressionDialog}
        viagogoVirtualId={viagogoVirtualId}
      />
    </FormProvider>
  );
};

export const ListingPricingFormV2 = memo(ListingPricingFormRawV2);

type ListingPricingFormReworkRawType = {
  /**
   * Row columns joint with ','
   *
   * Important!!!
   * React.memo track the props, only rerender when the props changes.
   * Dummy prop to force re-render the table row when columns/order changes.
   */
  columns: string;
  isExpanded: boolean;
  row: Row<ListingWithEvent>;
  rowIndex: number;
  getRowContent: (row: Row<ListingWithEvent>) => React.ReactNode;
  fromV2Table?: boolean;
};

const ListingPricingFormReworkRaw = ({
  row,
  rowIndex,
  getRowContent,
  columns,
  isExpanded,
  fromV2Table,
}: ListingPricingFormReworkRawType) => {
  const hasAllowSeatScoreCompListingMode = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );

  const componentName = useMemo(() => {
    if (fromV2Table) {
      return 'ListingPricingFormV2';
    }
    return 'ListingPricingFormV1';
  }, [fromV2Table]);
  useTrackRenderTime(componentName);

  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = useMemo(
    () => getUiCurrency(row.original?.listing?.currency),
    [getUiCurrency, row.original?.listing?.currency]
  );
  const { logEvent } = useClientLogContext();

  const rowData = useMemo(() => {
    if (row.original) {
      return {
        ...row.original,
        rowIndex: rowIndex,
      } as ListingWithEvent;
    }
    return null;
  }, [row.original, rowIndex]);

  // Memoize the default form values to avoid recreating on every render
  const defaultFormValues = useMemo(
    () => ({
      ...getListingDetailsUpdateInput(
        rowData?.listing as ListingDetails,
        uiCurrency,
        undefined,
        null,
        null,
        false,
        null,
        hasAllowSeatScoreCompListingMode
      ),
      isSubmitting: false,
      rowIndex: rowData?.rowIndex || 0,
      currentPageIndex: rowData?.currentPageIndex || 0,
    }),
    [
      rowData?.listing,
      uiCurrency,
      hasAllowSeatScoreCompListingMode,
      rowData?.rowIndex,
      rowData?.currentPageIndex,
    ]
  );

  const methods = useForm<ListingPricingInput>({
    defaultValues: defaultFormValues,
  });

  const createListingPricingInput = useCallback(
    (listing?: Listing | null) => ({
      ...getListingDetailsUpdateInput(
        listing as ListingDetails,
        uiCurrency,
        undefined,
        null,
        null,
        false,
        null,
        hasAllowSeatScoreCompListingMode
      ),
      isSubmitting: false,
      rowIndex: rowData?.rowIndex || 0,
      currentPageIndex: rowData?.currentPageIndex || 0,
    }),
    [
      hasAllowSeatScoreCompListingMode,
      rowData?.currentPageIndex,
      rowData?.rowIndex,
      uiCurrency,
    ]
  );

  // Track the listing ID to detect when it actually changes (not just object reference)
  const listingIdRef = useRef(rowData?.listing?.id);
  const listingVersionRef = useRef(rowData?.listing?.rowVer);
  // Nuance - this is necessary so the form picks up whenever the listing changed outside the table
  // like when ListingModal made edits to the data of this listing
  useEffect(() => {
    const currentListingId = rowData?.listing?.id;
    const currentListingVersion = rowData?.listing?.rowVer;

    // Only check for reset if listing ID or version actually changed
    const listingChanged =
      currentListingId !== listingIdRef.current ||
      currentListingVersion !== listingVersionRef.current;

    if (listingChanged) {
      listingIdRef.current = currentListingId;
      listingVersionRef.current = currentListingVersion;

      if (
        shouldResetForm(
          logEvent,
          rowData?.listing,
          methods.formState.defaultValues
        )
      ) {
        const newForm = createListingPricingInput(rowData?.listing);
        const { rowVersion: _, ...newFormWithoutVersion } = newForm;
        const { rowVersion, ...defaultWithoutVersion } =
          methods.formState.defaultValues ?? {};
        // Only reset when necessary, to persist the user input
        if (!isEqual(newFormWithoutVersion, defaultWithoutVersion)) {
          methods.reset(newForm);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Only depend on the actual data that matters - listing ID and version
    rowData?.listing?.id,
    rowData?.listing?.rowVer,
  ]);

  return (
    <FormProvider key={rowData?.listing?.id} {...methods}>
      {getRowContent(row)}
    </FormProvider>
  );
};

export const ListingPricingFormRework = memo(ListingPricingFormReworkRaw);
